import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadVerbsCsv } from './utils/loadVerbsCsv';
import { conjugatePastGeneric_Salim } from './utils/arPastGeneric_salim';
import { conjugatePresentFormI_Salim_fromRow } from './utils/arPresentFormI_salim';
import { conjugatePresentFormII_Salim_fromRow } from './utils/arPresentFormII_salim';
import { conjugatePresentFormIII_Salim_fromRow } from './utils/arPresentFormIII_salim';
import { conjugatePresentFormIV_Salim_fromRow } from './utils/arPresentFormIV_salim';
import { buildImperativeFromRow } from './utils/imperativeFormI_salim';
import { buildImperativeFromRowII } from './utils/imperativeFormII_salim';
import { buildImperativeFromRowIII } from './utils/imperativeFormIII_salim';
import { buildImperativeFromRowIV } from './utils/imperativeFormIV_salim';
import { buildParticiplesFromRoot } from './utils/participlesFormI_salim';
import { buildParticiplesFromRowNonFormI } from './utils/participlesNonFormI';
import {
  arrangeEntriesByBinyanRules,
  buildPrimaryHeavyBinyanRules,
  getLowerBinyanNumbers
} from './utils/analysisBinyanMixing';
import html2canvas from 'html2canvas';
import './VerbAnalysisGame.css';

const SOURCE_FORM_LABEL = 'המקור';
const SOURCE_PRONOUN_VALUE = '__source_no_pronoun__';
const SOURCE_PRONOUN_MESSAGE = 'אין לצורת המקור גוף';

const IMPERATIVE_PRONOUN_MAP = {
  'you_ms': 'أَنْتَ',
  'you_fs': 'أَنْتِ',
  'you_mp': 'أَنْتُمْ',
  'you_fp': 'أَنْتُنَّ'
};

const GENDER_OPTIONS = [
  { id: 'masculine', nameHeb: 'יחיד', nameAr: 'مفرد مذكر' },
  { id: 'feminine', nameHeb: 'יחידה', nameAr: 'مفرد مؤنث' },
  { id: 'plural', nameHeb: 'רבים', nameAr: 'جمع مذكر' },
  { id: 'pluralFeminine', nameHeb: 'רבות', nameAr: 'جمع مؤنث' }
];

const isAllowed = (allowedValue, genderId) => {
  if (!allowedValue || allowedValue.trim() === '') return false;
  
  const genderMap = {
    'masculine': 'sm',
    'feminine': 'sf',
    'plural': 'pm',
    'pluralFeminine': 'pf'
  };
  
  const allowedStr = allowedValue.toLowerCase().trim();
  if (allowedStr === 'all') return true;
  
  const genderCode = genderMap[genderId];
  if (!genderCode) return false;
  
  const allowedCodes = allowedStr.split(',').map(s => s.trim());
  return allowedCodes.includes(genderCode);
};

const MIXABLE_FORMS = ['עבר', 'הווה-עתיד', 'ציווי', 'בינוני פועל', 'בינוני פעול'];
const RANDOM_EXTRA_FORMS = ['עבר', 'הווה-עתיד', 'ציווי'];
const BINYAN_ONE_PRESENT_CHANCE = 0.6;

const shuffleArray = (items) => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const pickRandom = (items) => items[Math.floor(Math.random() * items.length)];
const arrangePrimaryOpeningQuestions = (questions, primaryBinyan) => {
  const primaryQuestions = shuffleArray(
    questions.filter(question => question.sourceBinyan === primaryBinyan)
  );
  const minorityQuestions = shuffleArray(
    questions.filter(question => question.sourceBinyan !== primaryBinyan)
  );
  const guaranteedFirstQuestions = primaryQuestions.splice(0, Math.min(2, primaryQuestions.length));
  const remainingBaseQuestions = [...primaryQuestions, ...minorityQuestions];

  for (let attempt = 0; attempt < MAX_STRICT_SELECTION_ATTEMPTS; attempt += 1) {
    const shuffledRemainingQuestions = shuffleArray(remainingBaseQuestions);
    const hasAdjacentMinorityQuestions = shuffledRemainingQuestions.some((question, index) => (
      index < shuffledRemainingQuestions.length - 1 &&
      question.sourceBinyan !== primaryBinyan &&
      shuffledRemainingQuestions[index + 1].sourceBinyan !== primaryBinyan
    ));

    if (!hasAdjacentMinorityQuestions || minorityQuestions.length <= 1) {
      return [...guaranteedFirstQuestions, ...shuffledRemainingQuestions];
    }
  }

  return [...guaranteedFirstQuestions, ...remainingBaseQuestions];
};
const MIXED_BINYAN_SOURCES = {
  '2': ['1', '1'],
  '3': ['1', '2']
};
const MAX_STRICT_SELECTION_ATTEMPTS = 80;
const getMixedSourcesForPrimary = (primaryBinyan, desiredCount = 2) => {
  if (primaryBinyan === '4') {
    return ['1', pickRandom(['2', '3'])];
  }

  const configuredSources = MIXED_BINYAN_SOURCES[primaryBinyan];
  if (configuredSources) {
    return [...configuredSources];
  }

  return shuffleArray(getLowerBinyanNumbers(primaryBinyan)).slice(0, desiredCount);
};

const getAllowedGendersForParticiple = (row, targetForm) => {
  const allowedField = targetForm === 'בינוני פועל' ? 'benoni_poel_allowed' : 'benoni_paul_allowed';
  const allowedValue = row[allowedField] || '';
  return GENDER_OPTIONS.filter(gender => isAllowed(allowedValue, gender.id)).map(gender => gender.id);
};

const DEFAULT_GAME_CONFIG = {
  title: 'ניתוח פעלים - עם בינוני פועל/פעול',
  summaryTitle: 'ניתוח פעלים - עם בינוני פועל/פעול',
  shareTitle: 'תוצאות ניתוח פעלים',
  shareTextLabel: 'ניתוח פעלים - עם בינוני פועל/פעול',
  shareFileName: 'תוצאות-ניתוח-פעלים-בניין-1.png',
  csvPath: '/verbs-grade10.csv',
  binyan: '1',
  allowedBinyans: ['1'],
  allowedTenses: ['עבר', 'הווה-עתיד', 'ציווי', 'בינוני פועל', 'בינוני פעול']
};

const BINYAN_GAME_CONFIGS = {
  '1': {
    allowedGizraPatterns: ['שלמים'],
    presentConjugator: conjugatePresentFormI_Salim_fromRow,
    imperativeBuilder: buildImperativeFromRow,
    participlesBuilder: buildParticiplesFromRoot,
    requiresFutureVowelForImperative: true,
  },
  '2': {
    title: 'ניתוח פעלים - בניין 2 עם בינוני פועל/פעול',
    summaryTitle: 'ניתוח פעלים - בניין 2 עם בינוני פועל/פעול',
    shareTextLabel: 'ניתוח פעלים - בניין 2 עם בינוני פועל/פעול',
    shareFileName: 'תוצאות-ניתוח-פעלים-בניין-2.png',
    allowedBinyans: ['2'],
    allowedGizraPatterns: ['שלמים', 'כפולים', 'ע"ו', 'פ"ו'],
    presentConjugator: conjugatePresentFormII_Salim_fromRow,
    imperativeBuilder: buildImperativeFromRowII,
    participlesBuilder: buildParticiplesFromRowNonFormI,
    requiresFutureVowelForImperative: false,
  },
  '3': {
    title: 'ניתוח פעלים - בניין 3 עם בינוני פועל/פעול',
    summaryTitle: 'ניתוח פעלים - בניין 3 עם בינוני פועל/פעול',
    shareTextLabel: 'ניתוח פעלים - בניין 3 עם בינוני פועל/פעול',
    shareFileName: 'תוצאות-ניתוח-פעלים-בניין-3.png',
    allowedBinyans: ['3'],
    allowedGizraPatterns: ['שלמים', 'ע"ו', 'פ"ו'],
    presentConjugator: conjugatePresentFormIII_Salim_fromRow,
    imperativeBuilder: buildImperativeFromRowIII,
    participlesBuilder: buildParticiplesFromRowNonFormI,
    requiresFutureVowelForImperative: false,
  },
  '4': {
    title: 'ניתוח פעלים - בניין 4 עם בינוני פועל/פעול',
    summaryTitle: 'ניתוח פעלים - בניין 4 עם בינוני פועל/פעול',
    shareTextLabel: 'ניתוח פעלים - בניין 4 עם בינוני פועל/פעול',
    shareFileName: 'תוצאות-ניתוח-פעלים-בניין-4.png',
    allowedBinyans: ['4'],
    allowedGizraPatterns: ['שלמים'],
    presentConjugator: conjugatePresentFormIV_Salim_fromRow,
    imperativeBuilder: buildImperativeFromRowIV,
    participlesBuilder: buildParticiplesFromRowNonFormI,
    requiresFutureVowelForImperative: false,
  }
};

function VerbAnalysisGameExtended({ gameConfig = DEFAULT_GAME_CONFIG }) {
  const navigate = useNavigate();
  const { grade } = useParams();
  const location = useLocation();
  const fromBinyan = location.state?.fromBinyan;
  const binyanDefaults = BINYAN_GAME_CONFIGS[gameConfig?.binyan || DEFAULT_GAME_CONFIG.binyan] || BINYAN_GAME_CONFIGS[DEFAULT_GAME_CONFIG.binyan];
  
  const getBackUrl = () => {
    if (fromBinyan) {
      return `/grade/${grade}/verbs/binyan/${fromBinyan}`;
    }
    return `/grade/${grade}/verbs`;
  };
  
  const config = {
    ...DEFAULT_GAME_CONFIG,
    ...binyanDefaults,
    ...gameConfig
  };
  const primaryBinyan = String(config.binyan || DEFAULT_GAME_CONFIG.binyan).replace('בניין ', '').trim();
  const {
    csvPath,
    shareFileName,
    shareTextLabel,
    shareTitle,
    summaryTitle,
    title
  } = config;
  
  const [gameVerbs, setGameVerbs] = useState([]);
  const [tenseOrder, setTenseOrder] = useState([]);
  const [genderOrder, setGenderOrder] = useState([]);
  const [currentVerbIndex, setCurrentVerbIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userAnswers, setUserAnswers] = useState({
    root: null,
    binyan: null,
    form: null,
    pronoun: null
  });
  const [feedback, setFeedback] = useState({
    root: null,
    binyan: null,
    form: null,
    pronoun: null
  });
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [showFixPrompt, setShowFixPrompt] = useState(false);
  const [gameHistory, setGameHistory] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [pulseFields, setPulseFields] = useState({});
  const [isUserSuccess, setIsUserSuccess] = useState(false);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');

  const audioContextRef = React.useRef(null);
  
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  };
  
  React.useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const pronouns = [
    { arabic: 'أَنَا', hebrew: 'أَنَا - מדבר/ת' },
    { arabic: 'أَنْتَ', hebrew: 'أَنْتَ - נוכח' },
    { arabic: 'أَنْتِ', hebrew: 'أَنْتِ - נוכחת' },
    { arabic: 'هُوَ', hebrew: 'هُوَ - נסתר' },
    { arabic: 'هِيَ', hebrew: 'هِيَ - נסתרת' },
    { arabic: 'نَحْنُ', hebrew: 'نَحْنُ - מדברים/ות' },
    { arabic: 'أَنْتُمْ', hebrew: 'أَنْتُمْ - נוכחים' },
    { arabic: 'أَنْتُنَّ', hebrew: 'أَنْتُنَّ - נוכחות' },
    { arabic: 'هُمْ', hebrew: 'هُمْ - נסתרים' },
    { arabic: 'هُنَّ', hebrew: 'هُنَّ - נסתרות' }
  ];

  const forms = [
    { id: 'עבר', name: 'עבר' },
    { id: 'הווה-עתיד', name: 'הווה-עתיד' },
    { id: 'ציווי', name: 'ציווי' },
    { id: 'בינוני פועל', name: 'בינוני פועל' },
    { id: 'בינוני פעול', name: 'בינוני פעול' },
    { id: SOURCE_FORM_LABEL, name: SOURCE_FORM_LABEL }
  ];

  const binyans = ['1 فَعَلَ', '2 فَعَّلَ', '3 فَاعَلَ', '4 أَفْعَلَ', '5 تَفَعَّلَ', '6 تَفَاعَلَ', '7 إِنْفَعَلَ', '8 إِفْتَعَلَ', '10 إِسْتَفْعَلَ '];

  const createBalancedQuestionPlan = () => {
    const questionPlan = [
      { form: 'עבר', sourceBinyan: primaryBinyan },
      { form: 'הווה-עתיד', sourceBinyan: primaryBinyan },
      { form: 'ציווי', sourceBinyan: primaryBinyan },
      { form: 'בינוני פועל', sourceBinyan: primaryBinyan },
      { form: 'בינוני פועל', sourceBinyan: primaryBinyan },
      { form: 'בינוני פעול', sourceBinyan: primaryBinyan },
      { form: 'בינוני פעול', sourceBinyan: primaryBinyan }
    ];

    const randomExtraForm = pickRandom(RANDOM_EXTRA_FORMS);
    questionPlan.push({ form: randomExtraForm, sourceBinyan: primaryBinyan });
    const mixedSources = getMixedSourcesForPrimary(primaryBinyan);

    if (mixedSources.length > 0) {
      if (primaryBinyan === '4') {
        const binyanOneForm = Math.random() < BINYAN_ONE_PRESENT_CHANCE
          ? 'הווה-עתיד'
          : pickRandom(MIXABLE_FORMS.filter(form => form !== 'הווה-עתיד'));
        const [secondaryBinyan] = mixedSources.filter(source => source !== '1');
        const secondaryForm = pickRandom(MIXABLE_FORMS.filter(form => form !== binyanOneForm));
        const binyanOneIndexes = questionPlan
          .map((question, index) => question.form === binyanOneForm ? index : -1)
          .filter(index => index !== -1);
        const secondaryIndexes = questionPlan
          .map((question, index) => (
            question.form === secondaryForm && question.sourceBinyan === primaryBinyan ? index : -1
          ))
          .filter(index => index !== -1);
        const binyanOneIndex = pickRandom(binyanOneIndexes);
        const secondaryIndex = pickRandom(secondaryIndexes);

        questionPlan[binyanOneIndex] = {
          ...questionPlan[binyanOneIndex],
          sourceBinyan: '1'
        };
        questionPlan[secondaryIndex] = {
          ...questionPlan[secondaryIndex],
          sourceBinyan: secondaryBinyan
        };

        return arrangeEntriesByBinyanRules(
          questionPlan,
          buildPrimaryHeavyBinyanRules(primaryBinyan)
        );
      }

      const shuffledMixedSources = shuffleArray([...mixedSources]);
      questionPlan[questionPlan.length - 1] = {
        ...questionPlan[questionPlan.length - 1],
        sourceBinyan: shuffledMixedSources[0]
      };

      const secondaryBinyanOneForm = pickRandom(MIXABLE_FORMS.filter(form => form !== randomExtraForm));
      const replaceableIndexes = questionPlan
        .map((question, index) => (
          question.form === secondaryBinyanOneForm && question.sourceBinyan === primaryBinyan ? index : -1
        ))
        .filter(index => index !== -1);
      const replacementIndex = pickRandom(replaceableIndexes);

      if (replacementIndex !== undefined) {
        questionPlan[replacementIndex] = {
          ...questionPlan[replacementIndex],
          sourceBinyan: shuffledMixedSources[1]
        };
      }

      if (primaryBinyan === '2') {
        return arrangePrimaryOpeningQuestions(questionPlan, primaryBinyan);
      }

      return arrangeEntriesByBinyanRules(
        questionPlan,
        buildPrimaryHeavyBinyanRules(primaryBinyan)
      );
    }

    return shuffleArray(questionPlan);
  };

  const getGameConfigForBinyan = (binyanValue) => {
    const normalizedBinyan = extractBinyanNumber(binyanValue) || primaryBinyan;
    return BINYAN_GAME_CONFIGS[normalizedBinyan] || null;
  };

  const getEligibleRowsForQuestion = (rows, targetForm, sourceBinyan) => {
    const sourceConfig = BINYAN_GAME_CONFIGS[sourceBinyan];
    if (!sourceConfig) return [];

    return rows.filter(row => {
      const rowBinyan = (row['בניין'] || '').trim();
      const gizra = (row['gizra'] || '').trim();
      const tense = (row['זמן'] || '').trim();
      const hasFaVowelFuture = (row['R2_FUTURE'] || row['fa_vowel_future'] || '').trim();
      const matchesBinyan = rowBinyan === sourceBinyan || rowBinyan === `בניין ${sourceBinyan}`;

      if (!matchesBinyan) return false;
      if (!sourceConfig.allowedGizraPatterns.some(pattern => gizra.includes(pattern))) return false;
      if (tense !== 'עבר') return false;
      if (sourceConfig.requiresFutureVowelForImperative && !hasFaVowelFuture) return false;

      if (targetForm === 'בינוני פעול') {
        return (row['benoni_paul_allowed'] || '').trim() !== '';
      }

      if (targetForm === 'בינוני פועל') {
        return (row['benoni_poel_allowed'] || '').trim() !== '';
      }

      return true;
    });
  };

  const buildQuestionSet = (rows, questionPlan, enforceUniqueParticipleGenders) => {
    const tenseSequence = [];
    const genderSequence = [];
    const selectedVerbs = [];
    const usedActiveGenders = new Set();
    const usedPassiveGenders = new Set();

    for (const { form: targetTense, sourceBinyan } of questionPlan) {
      const availableVerbs = getEligibleRowsForQuestion(rows, targetTense, sourceBinyan);
      const usedRoots = selectedVerbs.map(verb => verb['שורש']);
      const unusedVerbs = availableVerbs.filter(verb => !usedRoots.includes(verb['שורש']));
      const poolToUse = unusedVerbs.length > 0 ? unusedVerbs : availableVerbs;

      if (poolToUse.length === 0) {
        return null;
      }

      let selectedVerb = null;
      let selectedGender = null;

      if (targetTense === 'בינוני פועל' || targetTense === 'בינוני פעול') {
        const usedGenders = targetTense === 'בינוני פועל' ? usedActiveGenders : usedPassiveGenders;
        const candidates = poolToUse
          .map(verb => {
            const availableGenders = getAllowedGendersForParticiple(verb, targetTense);
            return {
              verb,
              availableGenders,
              unusedGenders: availableGenders.filter(gender => !usedGenders.has(gender))
            };
          })
          .filter(candidate => candidate.availableGenders.length > 0);

        if (candidates.length === 0) {
          return null;
        }

        const candidatesWithUnusedGenders = candidates.filter(candidate => candidate.unusedGenders.length > 0);

        if (enforceUniqueParticipleGenders && candidatesWithUnusedGenders.length === 0) {
          return null;
        }

        const candidatePool = enforceUniqueParticipleGenders
          ? candidatesWithUnusedGenders
          : (candidatesWithUnusedGenders.length > 0 ? candidatesWithUnusedGenders : candidates);

        const chosenCandidate = pickRandom(candidatePool);
        const genderPool = chosenCandidate.unusedGenders.length > 0
          ? chosenCandidate.unusedGenders
          : chosenCandidate.availableGenders;

        selectedVerb = chosenCandidate.verb;
        selectedGender = pickRandom(genderPool);

        if (selectedGender) {
          usedGenders.add(selectedGender);
        }
      } else {
        selectedVerb = pickRandom(poolToUse);
      }

      if (!selectedVerb) {
        return null;
      }

      selectedVerbs.push(selectedVerb);
      tenseSequence.push(targetTense);
      genderSequence.push(selectedGender);
    }

    return {
      selectedVerbs,
      tenseSequence,
      genderSequence
    };
  };

  useEffect(() => {
    const loadVerbs = async () => {
      try {
        setLoadError('');
        setIsLoaded(false);
        const rows = await loadVerbsCsv(csvPath);

        const shouldPreferUniqueParticipleGenders = primaryBinyan === '2' || primaryBinyan === '3';
        let selection = null;

        if (shouldPreferUniqueParticipleGenders) {
          for (let attempt = 0; attempt < MAX_STRICT_SELECTION_ATTEMPTS; attempt += 1) {
            const strictQuestionPlan = createBalancedQuestionPlan();
            selection = buildQuestionSet(rows, strictQuestionPlan, true);
            if (selection) {
              break;
            }
          }
        }

        if (!selection) {
          const fallbackQuestionPlan = createBalancedQuestionPlan();
          selection = buildQuestionSet(rows, fallbackQuestionPlan, false);
        }

        if (!selection) {
          setLoadError('לא נמצאו מספיק פעלים מתאימים לכל סוגי השאלות במשחק הזה.');
          return;
        }

        setGameVerbs(selection.selectedVerbs);
        setTenseOrder(selection.tenseSequence);
        setGenderOrder(selection.genderSequence);
        generateQuestion(selection.selectedVerbs, 0, selection.tenseSequence, selection.genderSequence);
        if (false) {

        const questionPlan = createBalancedQuestionPlan();
        const tenseSequence = [];
        const genderSequence = [];
        const selectedVerbs = [];

        for (const { form: targetTense, sourceBinyan } of questionPlan) {
          const availableVerbs = getEligibleRowsForQuestion(rows, targetTense, sourceBinyan);
          const usedRoots = selectedVerbs.map(v => v['שורש']);
          const unusedVerbs = availableVerbs.filter(v => !usedRoots.includes(v['שורש']));
          const poolToUse = unusedVerbs.length > 0 ? unusedVerbs : availableVerbs;

          if (poolToUse.length === 0) {
            setLoadError('לא נמצאו מספיק פעלים מתאימים לכל סוגי השאלות במשחק הזה.');
            return;
          }

          const randomVerb = pickRandom(poolToUse);
          selectedVerbs.push(randomVerb);
          tenseSequence.push(targetTense);

          if (targetTense === 'בינוני פועל' || targetTense === 'בינוני פעול') {
            const allowedField = targetTense === 'בינוני פועל' ? 'benoni_poel_allowed' : 'benoni_paul_allowed';
            const allowedValue = randomVerb[allowedField] || '';
            const availableGenders = GENDER_OPTIONS.filter(g => isAllowed(allowedValue, g.id));
            const randomGender = availableGenders.length > 0 ? pickRandom(availableGenders) : null;
            genderSequence.push(randomGender ? randomGender.id : 'masculine');
          } else {
            genderSequence.push(null);
          }
        }
        
        setGameVerbs(selectedVerbs);
        setTenseOrder(tenseSequence);
        setGenderOrder(genderSequence);
        generateQuestion(selectedVerbs, 0, tenseSequence, genderSequence);
        }
      } catch (error) {
        console.error('שגיאה בטעינת פעלים:', error);
        setLoadError('שגיאה בטעינת הפעלים למשחק.');
      } finally {
        setIsLoaded(true);
      }
    };
    
    loadVerbs();
    // Helpers used here are tied to the active game config and should rerun only when that config changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvPath, primaryBinyan]);

  const removeNiqqud = (str) => str.replace(/[\u064B-\u065F]/g, '');
  
  const extractBinyanNumber = (binyanStr) => {
    if (!binyanStr) return null;
    const match = binyanStr.match(/^\d+/);
    return match ? match[0] : binyanStr;
  };
  
  const getBinyanDisplay = (binyanNumber) => {
    if (!binyanNumber) return '';
    const number = extractBinyanNumber(binyanNumber);
    const found = binyans.find(b => extractBinyanNumber(b) === number);
    return found || binyanNumber;
  };

  const extractLetters = (verb) => {
    const clean = removeNiqqud(verb);
    return clean.split('').filter(c => /[\u0621-\u064A]/.test(c));
  };

  const generateRootOptions = (correctRoot, verb, binyan, pronoun) => {
    const options = [correctRoot];
    const verbLetters = extractLetters(verb);
    
    if (verbLetters.length === 3) {
      const letters = verbLetters;
      const permutations = new Set();
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
          for (let k = 0; k < 3; k++) {
            if (i !== j && j !== k && i !== k) {
              const perm = letters[i] + letters[j] + letters[k];
              if (perm !== correctRoot) {
                permutations.add(perm);
              }
            }
          }
        }
      }
      const permArray = Array.from(permutations).sort(() => Math.random() - 0.5);
      options.push(...permArray.slice(0, 3));
      if (options.length < 4) {
        const arabicLetters = 'بتثجحخدذرزسشصضطظعغفقكلمنهوي'.split('');
        let fillAttempts = 0;
        while (options.length < 4 && fillAttempts < 50) {
          fillAttempts++;
          const randomLetter = arabicLetters[Math.floor(Math.random() * arabicLetters.length)];
          const pos = Math.floor(Math.random() * 3);
          const newRoot = [...letters];
          newRoot[pos] = randomLetter;
          const candidate = newRoot.join('');
          if (candidate.length === 3 && candidate !== correctRoot && !options.includes(candidate)) {
            options.push(candidate);
          }
        }
      }
      return options.sort(() => Math.random() - 0.5);
    }
    
    {
      const wrongOptions = new Set();
      
      for (let i = 0; i <= verbLetters.length - 3; i++) {
        const consecutive = verbLetters.slice(i, i + 3).join('');
        if (consecutive !== correctRoot && !wrongOptions.has(consecutive)) {
          wrongOptions.add(consecutive);
        }
      }
      
      if (wrongOptions.size < 3) {
        if (verbLetters.length >= 4) {
          const skip1 = verbLetters[0] + verbLetters[2] + verbLetters[3];
          if (skip1 !== correctRoot && !wrongOptions.has(skip1)) {
            wrongOptions.add(skip1);
          }
        }
        
        if (verbLetters.length >= 5) {
          const skip2 = verbLetters[1] + verbLetters[3] + verbLetters[4];
          if (skip2 !== correctRoot && !wrongOptions.has(skip2)) {
            wrongOptions.add(skip2);
          }
        }
        
        if (verbLetters.length >= 5) {
          const skip3 = verbLetters[1] + verbLetters[2] + verbLetters[4];
          if (skip3 !== correctRoot && !wrongOptions.has(skip3)) {
            wrongOptions.add(skip3);
          }
        }
      }
      
      let attempts = 0;
      while (wrongOptions.size < 3 && attempts < 50) {
        attempts++;
        const shuffledLetters = [...verbLetters].sort(() => Math.random() - 0.5);
        const wrongRoot = shuffledLetters.slice(0, 3).join('');
        
        if (wrongRoot.length === 3 && wrongRoot !== correctRoot && !wrongOptions.has(wrongRoot)) {
          wrongOptions.add(wrongRoot);
        }
      }
      
      options.push(...Array.from(wrongOptions).slice(0, 3));
    }
    
    return options.sort(() => Math.random() - 0.5);
  };

  function generateQuestion(verbs, index, tenseSequence, genderSequence) {
    if (index >= verbs.length) {
      setShowResults(true);
      return;
    }
    
    const verbData = verbs[index];
    const targetTense = tenseSequence[index];
    const targetGender = genderSequence[index];
    
    let conjugatedVerb;
    let conjugations;
    let randomPronoun = null;
    let genderInfo = null;
    const questionConfig = getGameConfigForBinyan(verbData['בניין']);

    if (!questionConfig) {
      setLoadError('לא נמצאו שאלות מתאימות למשחק הזה.');
      return;
    }
    
    if (targetTense === 'עבר') {
      conjugations = conjugatePastGeneric_Salim(verbData['פועל']);
      if (conjugations) {
        const availablePronouns = pronouns.filter(p => conjugations[p.arabic]);
        randomPronoun = availablePronouns[Math.floor(Math.random() * availablePronouns.length)];
        conjugatedVerb = conjugations[randomPronoun.arabic];
      }
    } else if (targetTense === 'הווה-עתיד') {
      conjugations = questionConfig.presentConjugator ? questionConfig.presentConjugator(verbData) : null;
      if (conjugations) {
        const availablePronouns = pronouns.filter(p => conjugations[p.arabic]);
        randomPronoun = availablePronouns[Math.floor(Math.random() * availablePronouns.length)];
        conjugatedVerb = conjugations[randomPronoun.arabic];
      }
    } else if (targetTense === 'ציווי') {
      const imperativeConj = questionConfig.imperativeBuilder ? questionConfig.imperativeBuilder(verbData) : null;
      if (imperativeConj) {
        const imperativeKeys = ['you_ms', 'you_fs', 'you_mp', 'you_fp'];
        const randomKey = imperativeKeys[Math.floor(Math.random() * imperativeKeys.length)];
        conjugatedVerb = imperativeConj[randomKey];
        const arabicPronoun = IMPERATIVE_PRONOUN_MAP[randomKey];
        randomPronoun = pronouns.find(p => removeNiqqud(p.arabic) === removeNiqqud(arabicPronoun));
      }
    } else if (targetTense === 'בינוני פועל' || targetTense === 'בינוני פעול') {
      const participles = questionConfig.participlesBuilder ? questionConfig.participlesBuilder(verbData) : null;
      if (participles) {
        const gender = targetGender || 'masculine';
        if (targetTense === 'בינוני פועל') {
          conjugatedVerb = participles.active[gender];
        } else {
          conjugatedVerb = participles.passive[gender];
        }
        genderInfo = GENDER_OPTIONS.find(g => g.id === gender);
      }
    }
    
    if (!conjugatedVerb) {
      console.error('Failed to generate verb for:', targetTense, verbData);
      setLoadError('לא נמצאו שאלות מתאימות למשחק הזה.');
      return;
    }
    
    const rootOptions = generateRootOptions(
      removeNiqqud(verbData['שורש']), 
      conjugatedVerb,
      verbData['בניין'],
      randomPronoun ? randomPronoun.arabic : ''
    );
    
    const isBenoni = targetTense === 'בינוני פועל' || targetTense === 'בינוני פעול';
    const isMasdar = targetTense === SOURCE_FORM_LABEL;
    
    setCurrentQuestion({
      verb: conjugatedVerb,
      verbData: verbData,
      correctAnswers: {
        root: removeNiqqud(verbData['שורש']),
        binyan: verbData['בניין'],
        form: targetTense,
        pronoun: isMasdar ? SOURCE_PRONOUN_VALUE : (isBenoni ? (genderInfo ? genderInfo.id : null) : (randomPronoun ? randomPronoun.arabic : null))
      },
      options: {
        roots: rootOptions,
        binyans: binyans,
        forms: forms,
        pronouns: pronouns,
        genders: GENDER_OPTIONS
      },
      pronoun: randomPronoun,
      genderInfo: genderInfo,
      isBenoni: isBenoni,
      isMasdar: isMasdar
    });
    
    setUserAnswers({
      root: null,
      binyan: null,
      form: null,
      pronoun: null
    });
    
    setFeedback({
      root: null,
      binyan: null,
      form: null,
      pronoun: null
    });
    
    setShowCorrectAnswer(false);
    setShowFixPrompt(false);
    setAttempts([]);
    setPulseFields({});
    setIsUserSuccess(false);
    setTooltipVisible(false);
  }

  const checkAnswers = () => {
    if (!currentQuestion) return;
    
    const currentAttempt = { ...userAnswers };
    const newFeedback = {};
    let allCorrect = true;
    
    const isFirstAttempt = attempts.length === 0;
    
    const isRootCorrect = userAnswers.root === currentQuestion.correctAnswers.root;
    newFeedback.root = isRootCorrect ? 'correct' : 'wrong';
    if (!isRootCorrect) allCorrect = false;
    
    const isBinyanCorrect = extractBinyanNumber(userAnswers.binyan) === extractBinyanNumber(currentQuestion.correctAnswers.binyan);
    newFeedback.binyan = isBinyanCorrect ? 'correct' : 'wrong';
    if (!isBinyanCorrect) allCorrect = false;
    
    const isFormCorrect = userAnswers.form === currentQuestion.correctAnswers.form;
    newFeedback.form = isFormCorrect ? 'correct' : 'wrong';
    if (!isFormCorrect) allCorrect = false;
    
    let isPronounCorrect;
    if (currentQuestion.isMasdar) {
      isPronounCorrect = userAnswers.pronoun === currentQuestion.correctAnswers.pronoun;
      newFeedback.pronoun = isPronounCorrect ? 'correct' : 'wrong';
      if (!isPronounCorrect) allCorrect = false;
    } else if (currentQuestion.isBenoni) {
      isPronounCorrect = userAnswers.pronoun === currentQuestion.correctAnswers.pronoun;
      newFeedback.pronoun = isPronounCorrect ? 'correct' : 'wrong';
      if (!isPronounCorrect) allCorrect = false;
    } else {
      isPronounCorrect = removeNiqqud(userAnswers.pronoun || '') === removeNiqqud(currentQuestion.correctAnswers.pronoun);
      
      if (!isPronounCorrect && currentQuestion.correctAnswers.form === 'הווה-עתיד') {
        const userPronounClean = removeNiqqud(userAnswers.pronoun || '');
        const correctPronounClean = removeNiqqud(currentQuestion.correctAnswers.pronoun);
        
        const isUserAnta = userPronounClean === 'انت' || userPronounClean === 'أنت';
        const isUserHiya = userPronounClean === 'هي';
        const isCorrectAnta = correctPronounClean === 'انت' || correctPronounClean === 'أنت';
        const isCorrectHiya = correctPronounClean === 'هي';
        
        if ((isUserAnta && isCorrectHiya) || (isUserHiya && isCorrectAnta)) {
          isPronounCorrect = true;
        }
      }
      newFeedback.pronoun = isPronounCorrect ? 'correct' : 'wrong';
      if (!isPronounCorrect) allCorrect = false;
    }

    
    setFeedback(newFeedback);
    setAttempts(prev => [...prev, currentAttempt]);
    
    if (allCorrect) {
      setIsUserSuccess(true);
      playSuccessSound();
      setTimeout(() => {
        saveToHistory(isFirstAttempt);
        nextQuestion();
      }, 2000);
    } else {
      playErrorSound();
      setShowFixPrompt(true);
    }
  };

  const saveToHistory = (isCorrect) => {
    setGameHistory(prev => [...prev, {
      verb: currentQuestion.verb,
      verbData: currentQuestion.verbData,
      pronoun: currentQuestion.pronoun,
      genderInfo: currentQuestion.genderInfo,
      isBenoni: currentQuestion.isBenoni,
      isMasdar: currentQuestion.isMasdar,
      correctAnswers: currentQuestion.correctAnswers,
      userAttempts: [...attempts],
      finalAnswer: userAnswers,
      isCorrect
    }]);
  };

  const nextQuestion = () => {
    const nextIndex = currentVerbIndex + 1;
    setCurrentVerbIndex(nextIndex);
    generateQuestion(gameVerbs, nextIndex, tenseOrder, genderOrder);
  };

  const handleFix = () => {
    setShowFixPrompt(false);
  };

  const handleContinue = () => {
    setShowFixPrompt(false);
    setShowCorrectAnswer(true);
    
    const wrongFields = {};
    const updatedAnswers = { ...userAnswers };
    
    if (feedback.root === 'wrong') {
      wrongFields.root = true;
      updatedAnswers.root = currentQuestion.correctAnswers.root;
    }
    if (feedback.binyan === 'wrong') {
      wrongFields.binyan = true;
      updatedAnswers.binyan = getBinyanDisplay(currentQuestion.correctAnswers.binyan);
    }
    if (feedback.form === 'wrong') {
      wrongFields.form = true;
      updatedAnswers.form = currentQuestion.correctAnswers.form;
    }
    if (feedback.pronoun === 'wrong') {
      wrongFields.pronoun = true;
      updatedAnswers.pronoun = currentQuestion.correctAnswers.pronoun;
    }
    
    setUserAnswers(updatedAnswers);
    setPulseFields(wrongFields);
    setFeedback({
      root: 'correct',
      binyan: 'correct',
      form: 'correct',
      pronoun: 'correct'
    });
    
    saveToHistory(false);
  };
  
  const handleNextAfterCorrectAnswer = () => {
    setPulseFields({});
    setShowCorrectAnswer(false);
    nextQuestion();
  };

  const handleFormChange = (newForm) => {
    const prevForm = userAnswers.form;
    const wasParticiple = prevForm === 'בינוני פועל' || prevForm === 'בינוני פעול';
    const isNowParticiple = newForm === 'בינוני פועל' || newForm === 'בינוני פעול';
    const wasSourceForm = prevForm === SOURCE_FORM_LABEL;
    const isNowMasdar = newForm === SOURCE_FORM_LABEL;

    if (isNowMasdar) {
      setUserAnswers(prev => ({ ...prev, form: newForm, pronoun: SOURCE_PRONOUN_VALUE }));
      setFeedback(prev => ({ ...prev, pronoun: null }));
    } else if (wasSourceForm || wasParticiple !== isNowParticiple) {
      setUserAnswers(prev => ({ ...prev, form: newForm, pronoun: null }));
      setFeedback(prev => ({ ...prev, pronoun: null }));
    } else {
      setUserAnswers(prev => ({ ...prev, form: newForm }));
    }
    setTooltipVisible(false);
  };

  const handlePronounMouseMove = (e) => {
    if (!userAnswers.form && !showCorrectAnswer && !showFixPrompt && feedback.pronoun !== 'correct') {
      const isMobile = window.matchMedia('(max-width: 768px)').matches || e.pointerType === 'touch';
      
      if (isMobile) {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPosition({ 
          x: rect.left + rect.width / 2, 
          y: rect.top 
        });
      } else {
        setTooltipPosition({ x: e.clientX, y: e.clientY });
      }
      setTooltipVisible(true);
    }
  };

  const handlePronounMouseLeave = () => {
    setTooltipVisible(false);
  };

  const isSelectedFormParticiple = userAnswers.form === 'בינוני פועל' || userAnswers.form === 'בינוני פעול';
  const isSelectedFormMasdar = userAnswers.form === SOURCE_FORM_LABEL;
  const showPronounTooltip = tooltipVisible && false;

  const playSuccessSound = () => {
    initAudioContext();
    if (!audioContextRef.current) return;
    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2);
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  const playErrorSound = () => {
    initAudioContext();
    if (!audioContextRef.current) return;
    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  };

  const formatPronounOrGender = (value, { forResults = false } = {}) => {
    if (!value) return '-';
    if (value === SOURCE_PRONOUN_VALUE) {
      return SOURCE_PRONOUN_MESSAGE;
    }
    const genderOption = GENDER_OPTIONS.find(g => g.id === value);
    if (genderOption) return genderOption.nameHeb;
    const pronounOption = pronouns.find(p => removeNiqqud(p.arabic) === removeNiqqud(value));
    if (pronounOption) {
      const hebrewShort = pronounOption.hebrew.split(' - ')[1] || pronounOption.hebrew;
      return `${pronounOption.arabic} ${hebrewShort}`;
    }
    return value;
  };

  const renderMixedResultText = (text) => {
    const value = text == null ? '' : String(text);
    const arabicSegmentPattern = /([\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF](?:[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s־-]*[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF])?)/g;
    const arabicCharPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

    return value.split(arabicSegmentPattern).map((part, index) => {
      if (!part) return null;
      if (!arabicCharPattern.test(part)) return part;

      return (
        <span key={index} className="analysis-result-arabic-part">
          {part}
        </span>
      );
    });
  };

  const generateResultsImage = async () => {
    const resultsElement = document.querySelector('.words-summary');
    if (!resultsElement) return null;
    const canvas = await html2canvas(resultsElement, {
      backgroundColor: '#5f9ea0',
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const dataURLtoBlob = (dataURL) => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  };

  const shareViaWhatsApp = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;

    const blob = dataURLtoBlob(imageData);
    const fileName = shareFileName;
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `${shareTextLabel}\nהשגתי ${gameHistory.filter(h => h.isCorrect).length}/${gameHistory.length}!`,
          files: [file],
        });
      } catch (err) {
        console.log('Share canceled or failed', err);
      }
    } else {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = imageData;
      link.click();
    }
    setShowResultsPopup(false);
  };

  const shareViaEmail = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;
    const fileName = shareFileName;
    const link = document.createElement('a');
    link.download = fileName;
    link.href = imageData;
    link.click();
    setShowResultsPopup(false);
  };

  if (showResults) {
    const correctCount = gameHistory.filter(h => h.isCorrect).length;
    
    return (
      <div className="App verb-analysis-results">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={() => navigate(getBackUrl())} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>תוצאות המשחק</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary">
            <h3>{summaryTitle}</h3>
            <div className="final-score-display">
              ניקוד: {correctCount}/{gameHistory.length}
            </div>
            <br/>
            <div className="words-list">
              {gameHistory.map((item, idx) => {
                const binyanDisplay = getBinyanDisplay(item.correctAnswers.binyan);
                const isSourceForm = item.correctAnswers.form === SOURCE_FORM_LABEL;
                const pronounDisplay = isSourceForm ? '' : formatPronounOrGender(item.correctAnswers.pronoun, { forResults: true });
                const correctAnswerText = isSourceForm
                  ? `${item.correctAnswers.root}, ${binyanDisplay}, ${item.correctAnswers.form}`
                  : `${item.correctAnswers.root}, ${binyanDisplay}, ${item.correctAnswers.form}, ${pronounDisplay}`;
                
                let userAnswerText = '';
                if (!item.isCorrect && item.userAttempts.length > 0) {
                  const firstAttempt = item.userAttempts[0];
                  const userParts = [
                    firstAttempt.root || '-',
                    firstAttempt.binyan || '-',
                    firstAttempt.form || '-'
                  ];
                  if (!isSourceForm) {
                    userParts.push(formatPronounOrGender(firstAttempt.pronoun, { forResults: true }));
                  }
                  userAnswerText = userParts.join(', ');
                }
                
                return (
                  <div key={idx} className="word-item">
                    <div className="word-pair">
                      <span className="arabic-word-summary">{item.verb}</span>
                      <span className="separator"> - </span>
                      <span className="hebrew-word">
                        {renderMixedResultText(correctAnswerText)}
                      </span>
                    </div>
                    <div className={`attempts-info ${!item.isCorrect ? 'multiple-attempts' : ''}`}>
                      {item.isCorrect ? (
                        <span>✓ תשובה נכונה</span>
                      ) : (
                        <span>✗ תשובתך: {renderMixedResultText(userAnswerText)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="game-end-buttons">
              <button onClick={() => window.location.reload()} className="game-button restart-button">
                שחק שוב
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שתף תוצאות
              </button>
            </div>
          </div>

          {showResultsPopup && (
            <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button className="popup-close-button" onClick={() => setShowResultsPopup(false)}>
                  ×
                </button>
                <h3>שתף את התוצאות</h3>
                <p>בחר איך תרצה לשתף את התוצאות שלך:</p>
                <div className="popup-buttons">
                  <button onClick={shareViaEmail} className="popup-button email-button">
                    💾 הורד תמונה
                  </button>
                  <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                    📱 שתף בוואטסאפ
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  if (!isLoaded) {
    return <div className="loading">טוען...</div>;
  }

  if (loadError) {
    return (
      <div className="App">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={() => navigate(getBackUrl())} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>{title}</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary">
            <h3>{loadError}</h3>
          </div>
        </main>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div className="loading">טוען...</div>;
  }

  const allAnswered = userAnswers.root && userAnswers.binyan && userAnswers.form && userAnswers.pronoun;

  return (
    <div className="verb-analysis-game">
      <div className="game-header">
        <button onClick={() => navigate(getBackUrl())} className="back-to-verbs-button">
         ↩ חזרה למשחקי הפועל
        </button>
        <h2>{title}</h2>
        <div className="progress">{currentVerbIndex + 1}/{gameVerbs.length}</div>
      </div>

      <div className="verb-display">
        <div className="verb-arabic">{currentQuestion.verb}</div>
      </div>

      {isUserSuccess && (
        <div className="stars-animation">
          <div className="star">⭐</div>
          <div className="star">⭐</div>
          <div className="star">⭐</div>
        </div>
      )}

      <div className="analysis-grid">
        <div className={`analysis-section ${pulseFields.root ? 'pulse-container' : ''}`}>
          <label htmlFor="root-select">שורש</label>
          <select
            id="root-select"
            className={`analysis-dropdown ${
              feedback.root === 'wrong' ? 'wrong' : ''
            } ${feedback.root === 'correct' ? 'correct' : ''}`}
            value={userAnswers.root || ''}
            onChange={(e) => !showCorrectAnswer && !showFixPrompt && feedback.root !== 'correct' && setUserAnswers(prev => ({ ...prev, root: e.target.value }))}
            disabled={showCorrectAnswer || showFixPrompt || feedback.root === 'correct'}
          >
            <option value="">בחר שורש...</option>
            {currentQuestion.options.roots.map((root, idx) => (
              <option key={idx} value={root}>{root}</option>
            ))}
          </select>
        </div>

        <div className={`analysis-section ${pulseFields.binyan ? 'pulse-container' : ''}`}>
          <label htmlFor="binyan-select">בניין</label>
          <select
            id="binyan-select"
            className={`analysis-dropdown ${
              feedback.binyan === 'wrong' ? 'wrong' : ''
            } ${feedback.binyan === 'correct' ? 'correct' : ''}`}
            value={userAnswers.binyan || ''}
            onChange={(e) => !showCorrectAnswer && !showFixPrompt && feedback.binyan !== 'correct' && setUserAnswers(prev => ({ ...prev, binyan: e.target.value }))}
            disabled={showCorrectAnswer || showFixPrompt || feedback.binyan === 'correct'}
          >
            <option value="">בחר בניין...</option>
            {currentQuestion.options.binyans.map((binyan) => (
              <option key={binyan} value={binyan}>{binyan}</option>
            ))}
          </select>
        </div>

        <div className={`analysis-section ${pulseFields.form ? 'pulse-container' : ''}`}>
          <label htmlFor="form-select">צורה דקדוקית</label>
          <select
            id="form-select"
            className={`analysis-dropdown ${
              feedback.form === 'wrong' ? 'wrong' : ''
            } ${feedback.form === 'correct' ? 'correct' : ''}`}
            value={userAnswers.form || ''}
            onChange={(e) => !showCorrectAnswer && !showFixPrompt && feedback.form !== 'correct' && handleFormChange(e.target.value)}
            disabled={showCorrectAnswer || showFixPrompt || feedback.form === 'correct'}
          >
            <option value="">בחר צורה...</option>
            {currentQuestion.options.forms.map((form) => (
              <option key={form.id} value={form.id}>{form.name}</option>
            ))}
          </select>
        </div>

        <div className={`analysis-section ${pulseFields.pronoun ? 'pulse-container' : ''}`}>
          <label htmlFor="pronoun-select">{isSelectedFormParticiple ? 'מין ומספר' : 'גוף'}</label>
          <div 
            className="dropdown-wrapper"
            onMouseMove={handlePronounMouseMove}
            onMouseLeave={handlePronounMouseLeave}
          >
            <select
              id="pronoun-select"
              className={`analysis-dropdown ${
                feedback.pronoun === 'wrong' ? 'wrong' : ''
              } ${feedback.pronoun === 'correct' ? 'correct' : ''}${isSelectedFormMasdar ? ' source-form-locked' : ''}`}
              value={userAnswers.pronoun || ''}
              onChange={(e) => !showCorrectAnswer && !showFixPrompt && feedback.pronoun !== 'correct' && setUserAnswers(prev => ({ ...prev, pronoun: e.target.value }))}
              disabled={showCorrectAnswer || showFixPrompt || feedback.pronoun === 'correct' || isSelectedFormMasdar}
            >
              {isSelectedFormMasdar ? (
                <option value={SOURCE_PRONOUN_VALUE}>{SOURCE_PRONOUN_MESSAGE}</option>
              ) : (
                <>
                  <option value="">{isSelectedFormParticiple ? 'בחר מין ומספר...' : 'בחר גוף...'}</option>
                  {isSelectedFormParticiple ? (
                    currentQuestion.options.genders.map((gender) => (
                      <option key={gender.id} value={gender.id}>{gender.nameHeb}</option>
                    ))
                  ) : (
                    currentQuestion.options.pronouns.map((pronoun) => (
                      <option key={pronoun.arabic} value={pronoun.arabic}>{pronoun.hebrew}</option>
                    ))
                  )}
                </>
              )}
            </select>
            {showPronounTooltip && (
              <div 
                className="cursor-tooltip"
                style={{
                  left: tooltipPosition.x + 15,
                  top: tooltipPosition.y + 15
                }}
              >
                בחר קודם את הצורה הדקדוקית
              </div>
            )}
          </div>
        </div>
      </div>

      {!showFixPrompt && !showCorrectAnswer && Object.keys(pulseFields).length === 0 && (
        <button 
          className="check-button" 
          onClick={checkAnswers}
          disabled={!allAnswered}
        >
          בדיקה
        </button>
      )}

      {showFixPrompt && (
        <>
          <div className="overlay"></div>
          <div className="fix-prompt">
            <p>תרצו לתקן?</p>
            <div className="fix-buttons">
              <button onClick={handleFix} className="fix-yes">כן, אתקן</button>
              <button onClick={handleContinue} className="fix-no">לא, הצג תשובה נכונה</button>
            </div>
          </div>
        </>
      )}

      {Object.keys(pulseFields).length > 0 && (
        <button 
          className="check-button" 
          onClick={handleNextAfterCorrectAnswer}
        >
          המשך
        </button>
      )}

    </div>
  );
}

export default VerbAnalysisGameExtended;
